import { Website, WebsitePreviewEvent } from '@betterfunnel/types';
import Button from '@betterfunnel/ui-core/Button';
import FullScreenModal from '@betterfunnel/ui-core/FullScreenModal';
import Select from '@betterfunnel/ui-core/Select';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { applyExtraData } from '@betterfunnel/utils';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import {
  ArrowTopRightOnSquareIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import DashboardAnalyticsRangeSelector from '../components/DashboardAnalyticsRangeSelector';
import { WebsitePreview } from '../components/WebsitePreview';
import {
  AnalyticsRange,
  useDashboardContext,
} from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import { getWebsiteOnViewAnalytics } from '../services/AnalyticsService';
import {
  PREVIEW_ROOT,
  analyticsRangeToDates,
  classNames,
  emptyConverter,
} from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
  websiteId: string;
  defaultShowAnalytics?: boolean;
}

export default function WebsitePreviewModal({
  defaultShowAnalytics,
  open,
  onClose,
  websiteId,
}: Props) {
  const {
    analyticsRange,
    selectedDynamicPage,
    accountBrand,
    accountConfig,
    forms,
    globalSections,
  } = useDashboardContext();
  const { primaryDomain } = useContext(DynamicPageContext);

  const [device, setDevice] = useState('desktop');
  const [showAnalytics, setShowAnalytics] = useState(defaultShowAnalytics);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState<{
    websiteId: string;
    analyticsRange: AnalyticsRange;
    [key: string]: any;
  }>();
  const { selectedAccount } = useContext(UserContext);

  const [website, setWebsite] = useState<Website>();
  const [ready, setReady] = useState(false);

  const id = useMemo(() => uid(8), []);
  const sendMessageToIframe = useCallback(
    (message: any) => {
      const iframe = document.getElementById(id) as HTMLIFrameElement;
      if (iframe) {
        iframe.contentWindow?.postMessage(
          JSON.stringify(message),
          PREVIEW_ROOT,
        );
      }
    },
    [id],
  );

  useEffect(() => {
    if (open) {
      setShowAnalytics(defaultShowAnalytics);
      setAnalytics(undefined);
    }
  }, [defaultShowAnalytics, open]);

  useEffect(() => {
    if (
      !selectedDynamicPage ||
      !websiteId ||
      !forms ||
      !globalSections ||
      !ready
    ) {
      return;
    }

    const websiteRef = doc(
      selectedDynamicPage.ref,
      'websites',
      websiteId,
    ).withConverter(emptyConverter<Website>());
    const unsubscribe = onSnapshot(websiteRef, (website) => {
      if (!website.exists()) return;

      const enrichedWebsite = applyExtraData(
        website.data(),
        selectedDynamicPage.data(),
        accountBrand,
        accountConfig,
        globalSections.map((gs) => ({ ...gs.data().production, id: gs.id })),
      );

      enrichedWebsite.development.formIds =
        forms.map((f) => f.id) || enrichedWebsite.development.formIds;

      enrichedWebsite.development.forms = Object.fromEntries(
        forms?.map((f) => [f.id, f.data()]),
      );

      setWebsite(enrichedWebsite);

      sendMessageToIframe({
        eventName: 'websiteUpdated',
        website: enrichedWebsite.development,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [
    websiteId,
    selectedDynamicPage,
    accountBrand,
    accountConfig,
    ready,
    forms,
    globalSections,
    sendMessageToIframe,
  ]);

  useEffect(() => {
    if (!open || !showAnalytics) return;
    if (!selectedAccount || !selectedDynamicPage || !websiteId) return;

    if (
      analytics &&
      analytics.websiteId === websiteId &&
      analytics.analyticsRange === analyticsRange
    ) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
    setIsLoadingAnalytics(true);

    let relevant = true;
    getWebsiteOnViewAnalytics(
      selectedAccount.id,
      selectedDynamicPage.id,
      websiteId,
      startDate,
      endDate,
    )
      .then((res) => {
        if (!relevant) return;
        setAnalytics({ ...res, analyticsRange, websiteId });
      })
      .finally(() => {
        if (!relevant) return;
        setIsLoadingAnalytics(false);
      });

    return () => {
      relevant = false;
    };
  }, [
    websiteId,
    selectedAccount,
    selectedDynamicPage,
    open,
    showAnalytics,
    analyticsRange,
    analytics,
  ]);

  useEffect(() => {
    if (!showAnalytics || !open) {
      sendMessageToIframe({ eventName: 'hideAnalytics' });
      return;
    }

    if (analytics) {
      sendMessageToIframe({ eventName: 'showAnalytics', analytics });

      const handlePreviewEvent = (event: MessageEvent<string>) => {
        if (event.origin !== PREVIEW_ROOT) return;
        if (!selectedDynamicPage) return;

        try {
          const data = JSON.parse(event.data);
          if (data.eventName === 'ready') {
            sendMessageToIframe({ eventName: 'showAnalytics', analytics });
          }
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
      };
      window.addEventListener('message', handlePreviewEvent);

      return () => {
        window.removeEventListener('message', handlePreviewEvent);
      };
    }
  }, [
    showAnalytics,
    analytics,
    open,
    selectedDynamicPage,
    sendMessageToIframe,
  ]);

  const handlePreviewEvent = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== PREVIEW_ROOT) return;
      if (!selectedDynamicPage) return;
      const iframe = (document.getElementById(id) as HTMLIFrameElement)
        ?.contentWindow;
      if (event.source !== iframe) return;

      try {
        const data: WebsitePreviewEvent = JSON.parse(event.data);
        console.log('[WEBSITE_UPDATER] event', data?.eventName, data);

        if (data.eventName === 'ready') {
          console.warn(
            '[WEBSITE_PREVIEW_EVENT] ready event recieved',
            data.eventName,
            !!website,
          );
          setReady(true);
          return;
        }
      } catch (e) {
        Sentry.captureException(e);
        console.warn('[WEBSITE_PREVIEW_EVENT] failed to parse event data', e);
      }
    },
    [selectedDynamicPage, id, website],
  );

  useEffect(() => {
    window.addEventListener('message', handlePreviewEvent);

    return () => {
      window.removeEventListener('message', handlePreviewEvent);
    };
  }, [handlePreviewEvent]);

  return (
    <FullScreenModal open={open} onClose={() => {}}>
      <div className="h-full px-4 pt-5 pb-4 sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="secondary"
              size="large"
              icon={
                <ArrowLeftIcon className="-ml-0.5 h-4 w-4" aria-hidden="true" />
              }
              onClick={() => onClose()}
            >
              Exit
            </Button>
            <Toggle
              label={'Show analytics'}
              checked={showAnalytics}
              loading={isLoadingAnalytics}
              onChange={(v) => setShowAnalytics(v)}
            />
          </div>
          <div className="flex items-stretch gap-4">
            <DashboardAnalyticsRangeSelector size="large" />
            <Select
              selected={device}
              onChange={(v) => setDevice(v)}
              options={[
                {
                  value: 'desktop',
                  child: (
                    <div className="inline-flex items-center gap-x-1.5 px-2">
                      <ComputerDesktopIcon
                        className="-ml-0.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Desktop
                    </div>
                  ),
                },
                {
                  value: 'mobile',
                  child: (
                    <div className="inline-flex items-center gap-x-1.5 px-2">
                      <DevicePhoneMobileIcon
                        className="-ml-0.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Mobile
                    </div>
                  ),
                },
                {
                  value: 'tablet',
                  child: (
                    <div className="inline-flex items-center gap-x-1.5 px-2">
                      <DeviceTabletIcon
                        className="-ml-0.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Tablet
                    </div>
                  ),
                },
              ]}
            />
            <Button
              variant="secondary"
              size="large"
              icon={
                <QuestionMarkCircleIcon
                  className="-ml-0.5 h-4 w-4"
                  aria-hidden="true"
                />
              }
              onClick={() => {
                window.open('https://docs.betterfunnel.ai', '_blank');
              }}
            ></Button>
            <Button
              variant="secondary"
              size="large"
              icon={
                <ArrowTopRightOnSquareIcon
                  className="-ml-0.5 h-4 w-4"
                  aria-hidden="true"
                />
              }
              onClick={() =>
                window.open(
                  `https://${primaryDomain?.data().domain}/${
                    selectedDynamicPage?.data().path
                  }?utm=test_${websiteId}`,
                )
              }
            ></Button>
          </div>
        </div>
        <div
          className={classNames(
            'relative mx-auto rounded-[2.5rem] border-gray-800 bg-gray-800 transition-all',
            device === 'desktop' ? 'mt-4 h-full w-full' : '',
            device === 'mobile' ? 'mt-8 h-[780px] w-[390px] border-14' : '',
            device === 'tablet'
              ? 'mt-8 h-[454px] max-w-[341px] border-14 md:h-[682px] md:max-w-[512px]'
              : '',
          )}
        >
          {device !== 'desktop' ? (
            <>
              <div className="absolute top-[72px] -left-[17px] h-[32px] w-[3px] rounded-l-lg bg-gray-800"></div>
              <div className="absolute top-[124px] -left-[17px] h-[46px] w-[3px] rounded-l-lg bg-gray-800"></div>
              <div className="absolute top-[178px] -left-[17px] h-[46px] w-[3px] rounded-l-lg bg-gray-800"></div>
              <div className="absolute top-[142px] -right-[17px] h-[64px] w-[3px] rounded-r-lg bg-gray-800"></div>
            </>
          ) : (
            <></>
          )}
          <div
            className={classNames(
              'h-full w-full overflow-hidden bg-white',
              device !== 'desktop' ? 'rounded-4xl' : '',
            )}
          >
            <WebsitePreview websiteId={websiteId} id={id} />
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
}
